import { Controller } from "@hotwired/stimulus"

const DEFAULT_MAX_BYTES = Math.floor(1.5 * 1024 * 1024)

export default class MarkdownEditorController extends Controller {
  static targets = [
    "textarea",
    "fileInput",
    "writeTab",
    "previewTab",
    "writePane",
    "previewPane",
    "previewContent",
    "toolbar"
  ]

  static values = {
    uploadUrl: String,
    previewUrl: String,
    maxBytes: { type: Number, default: DEFAULT_MAX_BYTES },
    maxSizeMessage: { type: String, default: "Image must be 1.5 MB or smaller." },
    uploadingMessage: { type: String, default: "Uploading…" },
    uploadFailedMessage: { type: String, default: "Upload failed — please try again." },
    linkTextPrompt: { type: String, default: "Link text" },
    linkUrlPrompt: { type: String, default: "URL" },
    emptyPreviewMessage: { type: String, default: "Nothing to preview yet." },
    previewFailedMessage: { type: String, default: "Preview unavailable — please try again." }
  }

  declare readonly textareaTarget: HTMLTextAreaElement
  declare readonly fileInputTarget: HTMLInputElement
  declare readonly writeTabTarget: HTMLButtonElement
  declare readonly previewTabTarget: HTMLButtonElement
  declare readonly writePaneTarget: HTMLElement
  declare readonly previewPaneTarget: HTMLElement
  declare readonly previewContentTarget: HTMLElement
  declare readonly toolbarTarget: HTMLElement
  declare readonly hasFileInputTarget: boolean
  declare uploadUrlValue: string
  declare previewUrlValue: string
  declare maxBytesValue: number
  declare maxSizeMessageValue: string
  declare uploadingMessageValue: string
  declare uploadFailedMessageValue: string
  declare linkTextPromptValue: string
  declare linkUrlPromptValue: string
  declare emptyPreviewMessageValue: string
  declare previewFailedMessageValue: string

  private uploading = false
  private previewRequestId = 0

  showWrite(): void {
    this.writePaneTarget.hidden = false
    this.previewPaneTarget.hidden = true
    this.toolbarTarget.hidden = false
    this.setTabState("write")
    this.textareaTarget.focus()
  }

  async showPreview(): Promise<void> {
    this.writePaneTarget.hidden = true
    this.previewPaneTarget.hidden = false
    this.toolbarTarget.hidden = true
    this.setTabState("preview")
    await this.renderPreview()
  }

  bold(): void {
    this.wrapSelection("**", "**")
  }

  italic(): void {
    this.wrapSelection("*", "*")
  }

  heading(): void {
    this.prefixLines("## ")
  }

  quote(): void {
    this.prefixLines("> ")
  }

  bulletList(): void {
    this.prefixLines("- ")
  }

  code(): void {
    this.wrapSelection("`", "`")
  }

  link(): void {
    const text = window.prompt(this.linkTextPromptValue)?.trim()
    if (!text) return

    const url = window.prompt(this.linkUrlPromptValue)?.trim()
    if (!url) return

    this.insertAtCursor(`[${text}](${url})`)
  }

  pickImage(): void {
    if (this.uploading) return
    this.fileInputTarget.click()
  }

  async fileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]
    input.value = ""

    if (!file) return
    if (file.size > this.maxBytesValue) {
      window.alert(this.maxSizeMessageValue)
      return
    }

    await this.uploadImage(file)
  }

  private setTabState(mode: "write" | "preview"): void {
    const writeSelected = mode === "write"

    this.writeTabTarget.setAttribute("aria-selected", String(writeSelected))
    this.previewTabTarget.setAttribute("aria-selected", String(!writeSelected))

    if (writeSelected) {
      this.writeTabTarget.removeAttribute("data-variant")
      this.previewTabTarget.setAttribute("data-variant", "ghost")
    } else {
      this.writeTabTarget.setAttribute("data-variant", "ghost")
      this.previewTabTarget.removeAttribute("data-variant")
    }
  }

  private async renderPreview(): Promise<void> {
    const markdown = this.textareaTarget.value.trim()
    const requestId = ++this.previewRequestId

    if (!markdown) {
      this.previewContentTarget.innerHTML = `<p class="text-muted-foreground">${this.escapeHtml(this.emptyPreviewMessageValue)}</p>`
      return
    }

    this.previewContentTarget.innerHTML = `<p class="text-muted-foreground">${this.escapeHtml(this.uploadingMessageValue)}</p>`

    const token = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        body: JSON.stringify({ markdown }),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(token ? { "X-CSRF-Token": token } : {})
        },
        credentials: "same-origin"
      })

      if (requestId !== this.previewRequestId) return

      const payload = await response.json()

      if (!response.ok) {
        this.previewContentTarget.innerHTML = `<p class="text-muted-foreground">${this.escapeHtml(payload.error || this.previewFailedMessageValue)}</p>`
        return
      }

      const html = payload.html as string
      if (!html) {
        this.previewContentTarget.innerHTML = `<p class="text-muted-foreground">${this.escapeHtml(this.emptyPreviewMessageValue)}</p>`
        return
      }

      this.previewContentTarget.innerHTML = html
    } catch {
      if (requestId !== this.previewRequestId) return
      this.previewContentTarget.innerHTML = `<p class="text-muted-foreground">${this.escapeHtml(this.previewFailedMessageValue)}</p>`
    }
  }

  private async uploadImage(file: File): Promise<void> {
    this.uploading = true
    this.textareaTarget.setAttribute("aria-busy", "true")

    const formData = new FormData()
    formData.append("file", file)

    const token = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(this.uploadUrlValue, {
        method: "POST",
        body: formData,
        headers: {
          Accept: "application/json",
          ...(token ? { "X-CSRF-Token": token } : {})
        },
        credentials: "same-origin"
      })

      const payload = await response.json()

      if (!response.ok) {
        window.alert(payload.error || this.uploadFailedMessageValue)
        return
      }

      this.insertAtCursor(`\n${payload.placeholder as string}\n`)
    } catch {
      window.alert(this.uploadFailedMessageValue)
    } finally {
      this.uploading = false
      this.textareaTarget.removeAttribute("aria-busy")
    }
  }

  private wrapSelection(prefix: string, suffix: string): void {
    const textarea = this.textareaTarget
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selected = textarea.value.slice(start, end)
    const replacement = selected.length > 0 ? `${prefix}${selected}${suffix}` : `${prefix}${suffix}`

    this.replaceRange(replacement, start, end)

    if (selected.length === 0) {
      textarea.selectionStart = start + prefix.length
      textarea.selectionEnd = start + prefix.length
    }

    textarea.focus()
  }

  private prefixLines(prefix: string): void {
    const textarea = this.textareaTarget
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const value = textarea.value

    const lineStart = value.lastIndexOf("\n", start - 1) + 1
    const lineEndIndex = value.indexOf("\n", end)
    const lineEnd = lineEndIndex === -1 ? value.length : lineEndIndex
    const block = value.slice(lineStart, lineEnd)

    const prefixed = block
      .split("\n")
      .map((line) => (line.startsWith(prefix) ? line : `${prefix}${line}`))
      .join("\n")

    this.replaceRange(prefixed, lineStart, lineEnd)
    textarea.focus()
  }

  private insertAtCursor(text: string): void {
    const textarea = this.textareaTarget
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    this.replaceRange(text, start, end)
    const cursor = start + text.length
    textarea.selectionStart = cursor
    textarea.selectionEnd = cursor
    textarea.focus()
  }

  private replaceRange(replacement: string, start: number, end: number): void {
    const textarea = this.textareaTarget
    textarea.setRangeText(replacement, start, end, "end")
    textarea.dispatchEvent(new Event("input", { bubbles: true }))
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }
}
